import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle2, 
  X, 
  Crown, 
  Zap, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  Rocket,
  Building2,
  TrendingUp,
  Users
} from 'lucide-react';
import PageLayout, { PageHero } from './layout/PageLayout';
import DemoRequestModal from './DemoRequestModal';
import SEO from './SEO';
import { BACKEND_URL } from '../utils/backendURL';

const PricingPage = ({ onBack, onLogin, onRegister }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/plans/`);
      // Filter out BUSINESS_TRIAL and sort by price
      const filteredPlans = response.data
        .filter(plan => plan.plan_code !== 'BUSINESS_TRIAL' && plan.is_active)
        .sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0));
      setPlans(filteredPlans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = (planCode) => {
    if (planCode === 'TRIAL') {
      onRegister();
    } else if (planCode === 'ENTERPRISE') {
      window.history.pushState(null, '', '/contact');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else {
      onRegister();
    }
  };

  const getPlanIcon = (planCode) => {
    switch (planCode) {
      case 'TRIAL': return Zap;
      case 'STARTER': return Rocket;
      case 'GROWTH': return TrendingUp;
      case 'BUSINESS': return Building2;
      case 'ENTERPRISE': return Crown;
      default: return Sparkles;
    }
  };

  const getPlanGradient = (planCode) => {
    switch (planCode) {
      case 'TRIAL': return 'from-gray-500 to-gray-600';
      case 'STARTER': return 'from-blue-500 to-cyan-500';
      case 'GROWTH': return 'from-purple-500 to-blue-500';
      case 'BUSINESS': return 'from-amber-500 to-orange-500';
      case 'ENTERPRISE': return 'from-rose-500 to-pink-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getButtonText = (planCode) => {
    switch (planCode) {
      case 'TRIAL': return 'Start Free Trial';
      case 'ENTERPRISE': return 'Contact Sales';
      default: return 'Get Started';
    }
  };

  const formatPrice = (price, planCode, isYearly) => {
    if (planCode === 'TRIAL') return '0';
    if (planCode === 'ENTERPRISE') return `${Math.round(price * (isYearly ? 0.8 : 1)).toLocaleString()}`;
    const finalPrice = isYearly ? Math.round(price * 0.8) : price;
    return finalPrice.toLocaleString();
  };

  const formatTeamMembers = (count) => {
    if (count === -1) return 'Unlimited';
    return count.toString();
  };

  const formatProjects = (count) => {
    if (count === -1) return 'Unlimited';
    return count.toString();
  };

  const faqs = [
    {
      question: "What's included in the free trial?",
      answer: "Review the current free plan limits on this page. Any available trial duration is shown with the corresponding plan."
    },
    {
      question: "Can I switch plans later?",
      answer: "Yes! You can upgrade or downgrade your plan at any time. When upgrading, you'll get immediate access to new features. When downgrading, changes take effect at the next billing cycle."
    },
    {
      question: "What happens if I exceed my monthly execution limit?",
      answer: "Paid plans include overage pricing so you never get blocked. You'll be charged the overage rate per additional execution. Trial plans have hard limits."
    },
    {
      question: "Do you offer refunds?",
      answer: "Contact support to discuss billing or refund requests. Review the Terms of Service before purchasing."
    },
    {
      question: "What's included in Enterprise?",
      answer: "Review the published Enterprise plan details below and contact us to discuss your requirements before choosing a plan."
    }
  ];

  // Group plans for display - show Trial, Starter, Growth, Business in main grid, Enterprise separate
  const mainPlans = plans.filter(p => ['TRIAL', 'STARTER', 'GROWTH', 'BUSINESS'].includes(p.plan_code));
  const enterprisePlan = plans.find(p => p.plan_code === 'ENTERPRISE');

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Pricing - Lexa.run | AI-Powered Test Automation Plans"
        description="Choose the perfect plan for your team. From free trials to enterprise solutions, find the right AI-powered test automation plan for your needs."
      />

      <PageHero
        badge={<><Sparkles className="w-4 h-4" /> Simple, transparent pricing</>}
        title="Plans that scale"
        titleHighlight="with your team"
        subtitle="Start free and upgrade as you grow. All plans include our core AI-powered test generation features."
      >
        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <span className={`text-sm font-medium ${billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
            Monthly
          </span>
          <button
            role="switch" aria-label="Annual billing" aria-checked={billingPeriod === 'yearly'}
            onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingPeriod === 'yearly' ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <div
              className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                billingPeriod === 'yearly' ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingPeriod === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
            Yearly
            <Badge className="ml-2 bg-green-100 text-green-700 border-green-200">Save 20%</Badge>
          </span>
        </div>
      </PageHero>

      {/* Pricing Cards */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
            </div>
          ) : (
            <>
              {/* Main Plans Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {mainPlans.map((plan) => {
                  const isPopular = plan.popular;
                  const IconComponent = getPlanIcon(plan.plan_code);
                  const price = formatPrice(plan.price_usd, plan.plan_code, billingPeriod === 'yearly');

                  return (
                    <Card 
                      key={plan.plan_code}
                      className={`relative p-6 rounded-3xl border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                        isPopular 
                          ? 'border-purple-500 shadow-xl shadow-purple-500/20' 
                          : 'border-gray-200 hover:border-purple-200'
                      }`}
                    >
                      {(isPopular || plan.highlight_badge) && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 px-3 py-1 text-xs">
                            {plan.highlight_badge || 'Most Popular'}
                          </Badge>
                        </div>
                      )}

                      {/* Plan header */}
                      <div className="text-center mb-6">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${getPlanGradient(plan.plan_code)} text-white mb-3`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                        <p className="text-gray-500 text-sm">{plan.description}</p>
                      </div>

                      {/* Price */}
                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-gray-900">
                            ${price}
                          </span>
                          <span className="text-gray-500">/month</span>
                        </div>
                        {billingPeriod === 'yearly' && plan.price_usd > 0 && (
                          <p className="text-sm text-green-600 mt-1">
                            Billed ${(parseInt(price.replace(/,/g, '')) * 12).toLocaleString()}/year
                          </p>
                        )}
                        {plan.trial_days && (
                          <p className="text-sm text-purple-600 mt-1">
                            {plan.trial_days} days free trial
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-6 text-sm">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">
                            <strong>{plan.executions_monthly.toLocaleString()}</strong> executions/month
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">
                            <strong>{formatTeamMembers(plan.team_members_included)}</strong> team members
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">
                            <strong>{formatProjects(plan.max_projects)}</strong> project{plan.max_projects !== 1 ? 's' : ''}
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          {plan.overage_enabled ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={plan.overage_enabled ? 'text-gray-600' : 'text-gray-400'}>
                            Overage pricing {plan.overage_price_usd ? `($${plan.overage_price_usd}/exec)` : ''}
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          {plan.plan_code !== 'TRIAL' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={plan.plan_code !== 'TRIAL' ? 'text-gray-600' : 'text-gray-400'}>
                            Priority support
                          </span>
                        </li>
                      </ul>

                      {/* CTA */}
                      <Button
                        onClick={() => handleGetStarted(plan.plan_code)}
                        className={`w-full rounded-full py-5 font-semibold transition-all ${
                          isPopular
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg shadow-purple-500/25'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                        }`}
                      >
                        {getButtonText(plan.plan_code)}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Card>
                  );
                })}
              </div>

              {/* Enterprise Plan - Full Width */}
              {enterprisePlan && (
                <Card className="p-8 rounded-3xl border-2 border-violet-200 bg-gradient-to-br from-rose-50 to-pink-50 shadow-xl">
                  <div className="flex flex-col lg:flex-row items-center gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${getPlanGradient('ENTERPRISE')} text-white`}>
                          <Crown className="w-7 h-7" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900">{enterprisePlan.name}</h3>
                          <p className="text-gray-600">{enterprisePlan.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-4 bg-white/50 rounded-xl">
                          <div className="text-2xl font-bold text-gray-900">{enterprisePlan.executions_monthly.toLocaleString()}</div>
                          <div className="text-sm text-gray-600">Executions/mo</div>
                        </div>
                        <div className="text-center p-4 bg-white/50 rounded-xl">
                          <div className="text-2xl font-bold text-gray-900">Unlimited</div>
                          <div className="text-sm text-gray-600">Team Members</div>
                        </div>
                        <div className="text-center p-4 bg-white/50 rounded-xl">
                          <div className="text-2xl font-bold text-gray-900">Unlimited</div>
                          <div className="text-sm text-gray-600">Projects</div>
                        </div>
                        <div className="text-center p-4 bg-white/50 rounded-xl">
                          <div className="text-2xl font-bold text-gray-900">Contact us</div>
                          <div className="text-sm text-gray-600">Priority Support</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {['Discuss your requirements', 'Review plan limits', 'Contact our team'].map((feature) => (
                          <Badge key={feature} variant="outline" className="bg-white/50 text-gray-700 border-gray-300">
                            <CheckCircle2 className="w-3 h-3 mr-1 text-green-500" />
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="text-center lg:text-right">
                      <div className="mb-2">
                        <span className="text-sm text-gray-600">Starting at</span>
                      </div>
                      <div className="flex items-baseline justify-center lg:justify-end gap-1 mb-4">
                        <span className="text-5xl font-bold text-gray-900">
                          ${formatPrice(enterprisePlan.price_usd, 'ENTERPRISE', billingPeriod === 'yearly')}
                        </span>
                        <span className="text-gray-500">/month</span>
                      </div>
                      {billingPeriod === 'yearly' && (
                        <p className="text-sm text-green-600 mb-4">
                          Billed annually (Save 20%)
                        </p>
                      )}
                      <Button
                        onClick={() => handleGetStarted('ENTERPRISE')}
                        className="rounded-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-rose-500/25"
                      >
                        Contact Sales
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                      <p className="text-sm text-gray-500 mt-3">
                        Custom pricing available
                      </p>
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </section>

      {/* Feature Comparison Note */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">
            All plans include: <span className="font-medium text-gray-900">Flow Testing</span>, <span className="font-medium text-gray-900">API Testing</span>, <span className="font-medium text-gray-900">AI Test Generation</span>, <span className="font-medium text-gray-900">Test Scheduling</span>, and <span className="font-medium text-gray-900">CI/CD Integration</span>
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
              <HelpCircle className="w-4 h-4 mr-1" /> FAQ
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Frequently asked questions
            </h2>
            <p className="text-gray-600">
              Everything you need to know about pricing and billing
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="p-6 rounded-2xl border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Still have questions?
          </h2>
          <p className="text-xl text-purple-100 mb-10">
            Our team is here to help. Book a demo or chat with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setShowDemoModal(true)}
              size="lg"
              className="rounded-full bg-white text-purple-600 hover:bg-gray-100 px-10 py-6 text-lg font-semibold shadow-xl"
            >
              Book a Demo
            </Button>
            <Button 
              onClick={() => {
                window.history.pushState(null, '', '/contact');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              size="lg"
              variant="outline"
              className="rounded-full border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      <DemoRequestModal portalStyle
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />
    </PageLayout>
  );
};

export default PricingPage;
